import React, { useRef, useEffect, useCallback, useState } from 'react';
import type { FormFieldDefinition, FormAnswer } from '../../types/forms';
import { PhoneNumberInput } from '../PhoneNumberInput';
import { formatFullPhone, parsePhoneDigits } from '../../utils/phoneFormat';
import { BodyMapField } from './bodyMap/BodyMapField';

interface FormFieldProps {
  field: FormFieldDefinition;
  value: FormAnswer | undefined;
  onChange: (answer: FormAnswer) => void;
  error?: string;
}

// Site colors (src/hooks/useSiteColors.ts). Unselected controls are outlined in the
// muted text color so the control itself stays visible (WCAG 1.4.11).
const PRIMARY = 'var(--site-primary)';
const ON_PRIMARY = 'var(--site-on-primary)';
const CONTROL = 'var(--site-text-muted)';
const DANGER = 'var(--site-danger)';

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      style={{ fill: filled ? PRIMARY : 'none', stroke: filled ? PRIMARY : CONTROL }}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function SignatureField({
  value,
  onChange,
  error,
}: {
  value: FormAnswer | undefined;
  onChange: (answer: FormAnswer) => void;
  error?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const hasStrokes = useRef(false);

  const getPos = (e: MouseEvent | Touch, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ('clientX' in e ? e.clientX : e.clientX) - rect.left,
      y: ('clientY' in e ? e.clientY : e.clientY) - rect.top,
    };
  };

  const startDraw = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    isDrawing.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((x: number, y: number) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    hasStrokes.current = true;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
  }, []);

  const endDraw = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes.current) return;
    onChange({ type: 'text', value: canvas.toDataURL('image/png') });
  }, [onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onMouseDown = (e: MouseEvent) => {
      const { x, y } = getPos(e, canvas);
      startDraw(x, y);
    };
    const onMouseMove = (e: MouseEvent) => {
      const { x, y } = getPos(e, canvas);
      draw(x, y);
    };
    const onMouseUp = () => endDraw();

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const { x, y } = getPos(e.touches[0], canvas);
      startDraw(x, y);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const { x, y } = getPos(e.touches[0], canvas);
      draw(x, y);
    };
    const onTouchEnd = () => endDraw();

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, [startDraw, draw, endDraw]);

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasStrokes.current = false;
    onChange({ type: 'text', value: '' });
  };

  return (
    <div
      className={`relative rounded-xl border-2 overflow-hidden ${
        error ? 'border-site-danger/40' : 'border-site-border'
      }`}
      style={!error ? { borderColor: undefined } : undefined}
    >
      <canvas
        ref={canvasRef}
        width={600}
        height={160}
        // Signatures are saved as dark ink on a transparent image, so the pad stays white paper
        className="w-full touch-none bg-white"
        style={{ height: '160px' }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {!value?.type || (value.type === 'text' && !value.value) ? (
          // On the pad's white paper, not the site background
          <span className="text-slate-500 text-sm select-none">Sign here</span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={handleClear}
        className="absolute top-2 right-2 text-xs text-site-muted hover:text-site-text bg-site-bg border border-site-border rounded-lg px-2 py-1"
      >
        Clear
      </button>
    </div>
  );
}

function PhoneFormField({
  value,
  onChange,
  error,
  fieldId,
}: {
  value: FormAnswer | undefined;
  onChange: (answer: FormAnswer) => void;
  error?: string;
  fieldId: string;
}) {
  const textValue = value?.type === 'text' ? value.value : '';
  const [countryCode, setCountryCode] = useState('US');
  const [phone, setPhone] = useState(() =>
    textValue ? parsePhoneDigits(textValue.replace(/[^\d]/g, '').slice(-10)) : ''
  );

  const updatePhone = (digits: string, code: string = countryCode) => {
    setPhone(digits);
    onChange({ type: 'text', value: formatFullPhone(code, digits) });
  };

  return (
    <PhoneNumberInput
      id={`field-${fieldId}-phone`}
      countryCode={countryCode}
      phone={phone}
      onCountryCodeChange={code => {
        setCountryCode(code);
        updatePhone(phone, code);
      }}
      onPhoneChange={digits => updatePhone(digits)}
      error={error}
    />
  );
}

export function FormField({ field, value, onChange, error }: FormFieldProps) {
  const inputClass = `w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none ${
    error ? 'border-site-danger/40 focus:border-site-danger' : 'border-site-border'
  }`;

  const renderInput = () => {
    switch (field.type) {
      case 'short_text': {
        if (field.textFormat === 'phone') {
          return (
            <PhoneFormField
              value={value}
              onChange={onChange}
              error={error}
              fieldId={field.id}
            />
          );
        }

        const inputType =
          field.textFormat === 'email' ? 'email' : 'text';
        const inputMode =
          field.textFormat === 'number' ? 'numeric' : undefined;
        return (
          <input
            type={inputType}
            inputMode={inputMode}
            value={value?.type === 'text' ? value.value : ''}
            onChange={e => onChange({ type: 'text', value: e.target.value })}
            placeholder={field.placeholder}
            className={inputClass}
            style={!error ? { borderColor: undefined } : undefined}
          />
        );
      }

      case 'long_text':
        return (
          <textarea
            value={value?.type === 'text' ? value.value : ''}
            onChange={e => onChange({ type: 'text', value: e.target.value })}
            placeholder={field.placeholder}
            rows={4}
            className={`${inputClass} resize-none`}
            onInput={e => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = `${el.scrollHeight}px`;
            }}
          />
        );

      case 'single_select': {
        const selected = value?.type === 'options' ? value.values[0] : '';
        return (
          <div className="space-y-2">
            {field.options.map(option => {
              const isSelected = selected === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => onChange({ type: 'options', values: [option] })}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors text-left ${
                    isSelected ? 'border-current' : 'border-site-border'
                  }`}
                  style={isSelected ? { borderColor: PRIMARY } : undefined}
                >
                  <span
                    className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                    style={{
                      borderColor: isSelected ? PRIMARY : CONTROL,
                      backgroundColor: isSelected ? PRIMARY : 'transparent',
                    }}
                  >
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-site-on-primary" />
                    )}
                  </span>
                  <span className="text-sm text-site-text">{option}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'multi_select': {
        const selected = value?.type === 'options' ? value.values : [];
        const toggle = (option: string) => {
          const next = selected.includes(option)
            ? selected.filter(v => v !== option)
            : [...selected, option];
          onChange({ type: 'options', values: next });
        };
        return (
          <div className="space-y-2">
            {field.options.map(option => {
              const isChecked = selected.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggle(option)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors text-left ${
                    isChecked ? 'border-current' : 'border-site-border'
                  }`}
                  style={isChecked ? { borderColor: PRIMARY } : undefined}
                >
                  <span
                    className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0"
                    style={{
                      borderColor: isChecked ? PRIMARY : CONTROL,
                      backgroundColor: isChecked ? PRIMARY : 'transparent',
                    }}
                  >
                    {isChecked && (
                      <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                        <path d="M1 5l3 3 7-7" style={{ stroke: ON_PRIMARY }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span className="text-sm text-site-text">{option}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'consent': {
        const isChecked = value?.type === 'text' && value.value === 'true';
        return (
          <button
            type="button"
            onClick={() => onChange({ type: 'text', value: isChecked ? 'false' : 'true' })}
            className={`w-full flex items-start gap-3 px-4 py-3 rounded-xl border-2 transition-colors text-left ${
              isChecked ? 'border-current' : error ? 'border-site-danger/40' : 'border-site-border'
            }`}
            style={isChecked ? { borderColor: PRIMARY } : undefined}
          >
            <span
              className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5"
              style={{
                borderColor: isChecked ? PRIMARY : error ? DANGER : CONTROL,
                backgroundColor: isChecked ? PRIMARY : 'transparent',
              }}
            >
              {isChecked && (
                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                  <path d="M1 5l3 3 7-7" style={{ stroke: ON_PRIMARY }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="text-sm text-site-text">{field.placeholder || 'I agree'}</span>
          </button>
        );
      }

      case 'signature':
        return (
          <SignatureField
            value={value}
            onChange={onChange}
            error={error}
          />
        );

      case 'rating': {
        const rating = value?.type === 'text' ? parseInt(value.value, 10) : 0;
        return (
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                onClick={() => onChange({ type: 'text', value: String(star) })}
                className="transition-transform hover:scale-110"
                aria-label={`${star} star${star !== 1 ? 's' : ''}`}
              >
                <StarIcon filled={star <= rating} />
              </button>
            ))}
          </div>
        );
      }

      case 'linear_scale': {
        const scaleStart = field.scaleStart ?? 1;
        const scaleEnd = field.scaleEnd ?? 10;
        const selected =
          value?.type === 'text' && value.value !== ''
            ? parseInt(value.value, 10)
            : null;
        const scaleValues = Array.from(
          { length: scaleEnd - scaleStart + 1 },
          (_, i) => scaleStart + i,
        );
        const hasLabels = Boolean(field.scaleLabelStart || field.scaleLabelEnd);

        return (
          <div>
            <div
              className={`flex gap-1 sm:gap-2 ${error ? 'rounded-xl p-1 -m-1 border-2 border-site-danger/40' : ''}`}
            >
              {scaleValues.map(num => {
                const isSelected = selected === num;
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => onChange({ type: 'text', value: String(num) })}
                    className={`flex-1 min-w-0 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${
                      isSelected
                        ? 'text-site-on-primary'
                        : 'border-site-border text-site-text hover:border-site-muted'
                    }`}
                    style={
                      isSelected
                        ? { backgroundColor: PRIMARY, borderColor: PRIMARY }
                        : undefined
                    }
                    aria-label={String(num)}
                    aria-pressed={isSelected}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
            {hasLabels && (
              <div className="flex justify-between mt-1.5 text-xs text-site-muted">
                <span>{field.scaleLabelStart}</span>
                <span>{field.scaleLabelEnd}</span>
              </div>
            )}
          </div>
        );
      }

      case 'body_map':
        return <BodyMapField field={field} value={value} onChange={onChange} error={error} />;

      default:
        return null;
    }
  };

  if (field.type === 'consent' || field.type === 'body_map') {
    return (
      <div>
        <div className="text-sm font-medium text-site-text mb-2">
          {field.label}
          {field.required && <span className="text-site-danger ml-1">*</span>}
        </div>
        {field.type === 'body_map' && field.placeholder && (
          <p className="text-sm text-site-muted -mt-1 mb-3">{field.placeholder}</p>
        )}
        {renderInput()}
        {error && <p className="text-site-danger text-sm mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-site-text mb-2">
        {field.label}
        {field.required && <span className="text-site-danger ml-1">*</span>}
      </label>
      {renderInput()}
      {error && <p className="text-site-danger text-sm mt-1">{error}</p>}
    </div>
  );
}
